import * as yup from 'yup';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-form-state-manager';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import { DateTimeForm } from '../Common/DateRangeForm';
import { getServerErrors } from '../../common/helpers';
import { useNotifier } from '../../common/Notifications/NotificationProvider';
import DuplicateEventMutation from './DuplicateEventMutation';
import UI from '../../common/UI';
import useMutation from '../../api/useMutation';
import useValidation from '../../common/useValidation';

const duplicateEventSchema = yup.object({
  title: yup.string().ensure().required(),
  start_date: yup.string().ensure().required(),
  location: yup.object().required().shape({
    id: yup.string().required(),
  }),
});

interface Event {
  id: string;
  title: string;
  start_date: string;
  location: {
    id?: string | null;
    title?: string | null;
    description?: string | null;
    address?: string | null;
    country?: string | null;
    coordinates: number[];
  };
}

export interface DuplicateEventFormProps {
  event: Event;
  onSuccess?: () => void;
}

export const DuplicateEventForm = ({ event, onSuccess }: DuplicateEventFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();

  const form = useForm({
    values: {
      title: event.title,
      location: {
        id: event.location.id,
        title: event.location.title,
        description: event.location.description,
        address: event.location.address,
        country: event.location.country,
        coordinates: event.location.coordinates,
      },
      start_date: event.start_date,
      include_start_groups: false,
      include_messages: false,
      include_scanners: false,
      include_export_profiles: false,
      include_coupons: false,
      include_invitations: false,
    },
  });

  const [duplicateEvent, { loading, error }] = useMutation(DuplicateEventMutation, {
    variables: {
      input: {
        ...form.values,
        event: { id: event.id },
      },
    },
    onCompleted: () => {
      onSuccess?.();
      notifier.success(t('notifications.event_duplicated'));
    },
  });

  const { valid, errors } = useValidation({
    schema: duplicateEventSchema,
    values: {
      event: { id: event.id },
      ...form.values,
    },
    externalErrors: getServerErrors(error),
  });

  const locationBlurTimerRef = useRef<number>();

  // Clear timer on unmount.
  useEffect(() => () => window.clearTimeout(locationBlurTimerRef.current), []);

  const handleLocationBlur = () => {
    window.clearTimeout(locationBlurTimerRef.current);

    // Save the touched event after a small delay, to prevent flashing of validation errors.
    locationBlurTimerRef.current = window.setTimeout(() => {
      form.setTouched('location', true);
    }, 300);
  };

  return (
    <UI.Form onSubmit={() => duplicateEvent()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>
            {t('duplicate_event', { event: event.title })}
          </UI.H4>

          <UI.Paragraph sc={{ mb: 0 }}>
            {t('duplicate_event_description')}
          </UI.Paragraph>
        </UI.Div>

        <UI.ServerErrors error={error} />

        <UI.HR />

        <UI.InputGroup sc={{ valid: !errors.title, touched: form.touched.title }}>
          <UI.InputLabel htmlFor="EventTitle">
            {t('event_title')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.DebouncedInput
            {...form.text('title')}
            placeholder={t('event_title_placeholder')}
            id="EventTitle"
          />
          <UI.ErrorMessages attribute={t('event_title')} errors={errors.title} />
        </UI.InputGroup>

        <UI.InputGroup sc={{ valid: !errors['location.id'], touched: form.touched.location }}>
          <UI.InputLabel htmlFor="location">
            {t('location')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.GoogleLocationInput
            id="location"
            value={form.values.location}
            onChange={(location) => form.set('location', location)}
            onBlur={() => handleLocationBlur()}
            placeholder={t('location_placeholder')}
          />
          <UI.ErrorMessages attribute={t('location')} errors={errors['location.id']} />
        </UI.InputGroup>

        <DateTimeForm
          form={form}
          errors={errors}
          keys={{ date: 'start_date', time: 'start_time' }}
          require
        />

        <UI.InputGroup>
          <UI.InputLabel>
            {t('copy_along')}
          </UI.InputLabel>
          <UI.GridContainer sc={{ columns: '1fr 1fr' }}>
            <UI.Div>
              <UI.Checkbox {...form.checkbox('include_coupons')}>
                {t('coupons')}
              </UI.Checkbox>
              <UI.Checkbox {...form.checkbox('include_invitations')}>
                {t('invitation_codes')}
              </UI.Checkbox>
              <UI.Checkbox {...form.checkbox('include_start_groups')}>
                {t('ticket_number_sequences')}
              </UI.Checkbox>
            </UI.Div>
            <UI.Div>
              <UI.Checkbox {...form.checkbox('include_messages')}>
                {t('messages')}
              </UI.Checkbox>
              <UI.Checkbox {...form.checkbox('include_scanners')}>
                {t('scanners')}
              </UI.Checkbox>
              <UI.Checkbox {...form.checkbox('include_export_profiles')}>
                {t('export_profiles')}
              </UI.Checkbox>
            </UI.Div>
          </UI.GridContainer>
        </UI.InputGroup>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('duplicate')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

interface UseDuplicateEventModalProps {
  event: Event;
  onSuccess?: () => void;
}

const useDuplicateEventModal = ({ event, onSuccess }: UseDuplicateEventModalProps) => {
  const [openModal, closeModal] = useModal(() => {
    const handleSuccess = () => {
      closeModal();
      onSuccess?.();
    };

    return (
      <UI.Modal maxWidth={600} close={closeModal}>
        <DuplicateEventForm event={event} onSuccess={handleSuccess} />
      </UI.Modal>
    );
  }, [event]);

  return [openModal, closeModal];
};

export default useDuplicateEventModal;
